import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Clock, ChevronRight, ChevronDown, ArrowLeft,
  Plus, Users, Star, CheckCircle, X, Trash2, FileText, Upload,
  MessageCircle, Loader2, BookOpen
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const EVENT_TYPES = {
  orientation: { label: 'Orientation / Onboarding', color: '#E86C3D', icon: '🎯', description: 'Program kickoff, multi-day welcome, cohort assignments' },
  course: { label: 'Development Workshop', color: '#10B981', icon: '📚', description: 'Recurring skill-building sessions (in-person, virtual, hybrid)' },
  speaker: { label: 'Speaker / Guest Event', color: '#8B5CF6', icon: '🎤', description: 'Guest speakers, panels, career talks, industry visits' },
  milestone: { label: 'Check-In / Milestone', color: '#F59E0B', icon: '📋', description: 'Progress reviews, mid-program check-ins, portfolio reviews' },
  celebration: { label: 'Celebration / Completion', color: '#EC4899', icon: '🎉', description: 'End-of-program ceremonies, awards, showcases' },
  custom: { label: 'Custom Event', color: '#6B7280', icon: '📅', description: 'Any other event type' }
};

// Template defaults for quick event creation
const EVENT_TEMPLATES = {
  orientation: {
    title: '', description: 'Welcome to the program! This orientation will prepare you for an amazing experience.',
    checkin_required: true,
    survey_questions: [
      { question: 'How would you rate the orientation overall?', type: 'rating' },
      { question: 'What are you most excited about?', type: 'text' },
      { question: 'Is there anything you need help with before the program starts?', type: 'text' }
    ],
    schedule: []
  },
  course: {
    title: '', description: 'Interactive skill-building course.',
    checkin_required: true,
    survey_questions: [
      { question: 'How useful was this course?', type: 'rating' },
      { question: 'What was your biggest takeaway?', type: 'text' }
    ],
    schedule: []
  },
  speaker: {
    title: '', description: 'Guest speaker event.',
    checkin_required: true,
    survey_questions: [
      { question: 'How inspiring was the speaker?', type: 'rating' },
      { question: 'What was your favorite part?', type: 'text' },
      { question: 'Would you want to learn more about this career?', type: 'rating' }
    ],
    schedule: []
  },
  milestone: {
    title: '', description: 'Progress check-in and review.',
    checkin_required: false,
    survey_questions: [
      { question: 'How do you feel about your progress so far?', type: 'rating' },
      { question: 'What goals are you focused on right now?', type: 'text' },
      { question: 'What support do you need from your mentor?', type: 'text' }
    ],
    schedule: []
  },
  celebration: {
    title: '', description: 'Celebrating your achievements!',
    checkin_required: true,
    survey_questions: [
      { question: 'How would you rate the overall program experience?', type: 'rating' },
      { question: 'What was your proudest moment?', type: 'text' },
      { question: 'What advice would you give to next year\'s participants?', type: 'text' }
    ],
    schedule: []
  },
  custom: {
    title: '', description: '', checkin_required: false, survey_questions: [], schedule: []
  }
};

const EventsPage = ({ user }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [expandedDay, setExpandedDay] = useState(0);
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [checkingIn, setCheckingIn] = useState(null);
  const [surveyForm, setSurveyForm] = useState({ rating: 0, feedback: '', responses: {} });
  const [form, setForm] = useState({ title: '', description: '', event_type: 'orientation', location: '', start_date: '', end_date: '', schedule: [], cohorts: [], survey_questions: [], checkin_required: true, virtual_link: '' });
  const [dayForm, setDayForm] = useState({ day_title: '', day_date: '', sessions: [{ time: '', title: '', description: '', location: '', type: 'course', cohort: '' }] });
  const isStaff = ['lead_monitor', 'program_admin', 'admin', 'platform_admin', 'coach'].includes(user?.role);

  const applyTemplate = (eventType) => {
    const tpl = EVENT_TEMPLATES[eventType] || EVENT_TEMPLATES.custom;
    setForm(prev => ({ ...prev, event_type: eventType, description: tpl.description, checkin_required: tpl.checkin_required, survey_questions: tpl.survey_questions }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/api/gps/events`, { headers: hdr() });
      if (res.ok) setEvents(await res.json());
    } catch (e) { console.error('Events fetch failed', e); }
    setLoading(false);
  };

  const checkinToSession = async (eventId, sessionId) => {
    setCheckingIn(sessionId);
    try {
      const res = await fetch(`${API}/api/gps/events/${eventId}/checkin`, { method: 'POST', headers: hdr(), body: JSON.stringify({ session_id: sessionId }) });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.points_earned ? `Checked in! +${d.points_earned} pts` : 'Checked in!');
        // Refresh event detail
        const evRes = await fetch(`${API}/api/gps/events/${eventId}`, { headers: hdr() });
        if (evRes.ok) setSelectedEvent(await evRes.json());
      }
    } catch (e) { toast.error('Check-in failed'); }
    setCheckingIn(null);
  };

  const submitSurvey = async () => {
    if (surveyForm.rating === 0) { toast.error('Please give a rating'); return; }
    try {
      const res = await fetch(`${API}/api/gps/events/${selectedEvent.id}/survey`, { method: 'POST', headers: hdr(), body: JSON.stringify(surveyForm) });
      if (res.ok) { toast.success('Survey submitted! +10 pts'); setShowSurvey(false); fetchEvents(); const evRes = await fetch(`${API}/api/gps/events/${selectedEvent.id}`, { headers: hdr() }); if (evRes.ok) setSelectedEvent(await evRes.json()); }
    } catch (e) { toast.error('Failed'); }
  };

  const createEvent = async () => {
    if (!form.title || !form.start_date) { toast.error('Title and start date required'); return; }
    try {
      const res = await fetch(`${API}/api/gps/events`, { method: 'POST', headers: hdr(), body: JSON.stringify(form) });
      if (res.ok) { toast.success('Event created!'); setShowCreateModal(false); fetchEvents(); }
    } catch (e) { toast.error('Failed'); }
  };

  const addDayToSchedule = () => {
    if (!dayForm.day_title) return;
    setForm({ ...form, schedule: [...form.schedule, { ...dayForm }] });
    setDayForm({ day_title: '', day_date: '', sessions: [{ time: '', title: '', description: '', location: '', type: 'course', cohort: '' }] });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}><Calendar className="w-10 h-10 animate-pulse text-orange-500" /></div>;

  // ==================== EVENT DETAIL VIEW ====================
  if (selectedEvent) {
    const ev = selectedEvent;
    const evType = EVENT_TYPES[ev.event_type] || EVENT_TYPES.custom;
    const myCheckins = ev.my_checkins || [];
    const cohorts = ev.cohorts || [];
    const hasCohorts = cohorts.length > 0;

    const getFilteredSessions = (sessions) => {
      if (!hasCohorts || selectedCohort === 'all') return sessions || [];
      return (sessions || []).filter(s => !s.cohort || s.cohort === '' || s.cohort === selectedCohort || s.cohort === 'all');
    };

    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${evType.color}, ${evType.color}CC)` }}>
          <div className="max-w-2xl mx-auto p-6">
            <button onClick={() => setSelectedEvent(null)} className="flex items-center gap-2 text-gray-800 hover:text-gray-900 mb-4" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
            <span className="text-3xl mb-2 block">{evType.icon}</span>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{ev.title}</h1>
            {ev.description && <p className="text-gray-800 text-sm mb-3">{ev.description}</p>}
            <div className="flex flex-wrap gap-3 text-white/90 text-sm">
              {ev.location && <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full"><MapPin className="w-3 h-3" /> {ev.location}</div>}
              {ev.start_date && <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full"><Calendar className="w-3 h-3" /> {new Date(ev.start_date).toLocaleDateString()}{ev.end_date && ev.end_date !== ev.start_date ? ` — ${new Date(ev.end_date).toLocaleDateString()}` : ''}</div>}
              <button onClick={() => {
                const start = ev.start_date ? ev.start_date.replace(/-/g, '') : '';
                const end = ev.end_date ? ev.end_date.replace(/-/g, '') : start;
                const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&dates=${start}/${end}&location=${encodeURIComponent(ev.location || '')}&details=${encodeURIComponent(ev.description || '')}`;
                window.open(gcalUrl, '_blank');
              }} className="flex items-center gap-1.5 bg-white/25 hover:bg-white/35 px-3 py-1 rounded-full transition-all cursor-pointer" data-testid="add-to-calendar">
                <Plus className="w-3 h-3" /> Add to Calendar
              </button>
              {ev.virtual_link && <a href={ev.virtual_link} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-emerald-500/30 px-3 py-1 rounded-full hover:bg-emerald-500/40">Join Virtual</a>}
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
          {/* Orientation Workbook Link — only for orientation events */}
          {ev.event_type === 'orientation' && (
            <Card className="p-4 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/workbook')} data-testid="workbook-link">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-gray-900">Orientation Workbook</p>
                  <p className="text-xs text-gray-500">Complete your course activity sheets digitally</p>
                </div>
                <ChevronRight className="w-5 h-5 text-orange-400" />
              </div>
            </Card>
          )}

          {/* Cohort Filter */}
          {hasCohorts && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              <button onClick={() => setSelectedCohort('all')} className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${selectedCohort === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All Groups</button>
              {cohorts.map(c => (
                <button key={c} onClick={() => setSelectedCohort(c)} className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${selectedCohort === c ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  style={selectedCohort === c ? { backgroundColor: evType.color } : {}}>{c}</button>
              ))}
            </div>
          )}

          {/* Schedule Days */}
          {(ev.schedule || []).map((day, dayIdx) => (
            <Card key={dayIdx} className="rounded-2xl bg-white shadow-sm overflow-hidden">
              <button onClick={() => setExpandedDay(expandedDay === dayIdx ? null : dayIdx)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                  style={{ backgroundColor: `${evType.color}15`, color: evType.color }}>D{dayIdx + 1}</div>
                <div className="flex-1">
                  <p className="font-bold text-white">{day.day_title}</p>
                  <p className="text-xs text-gray-500">{day.day_date ? new Date(day.day_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : ''}</p>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedDay === dayIdx ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {expandedDay === dayIdx && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="px-4 pb-4 space-y-2">
                      {getFilteredSessions(day.sessions).map((session, sIdx) => {
                        const sessionId = `${dayIdx}_${sIdx}`;
                        const isCheckedIn = myCheckins.includes(sessionId);
                        return (
                          <motion.div key={sIdx} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: sIdx * 0.05 }}
                            className={`p-3 rounded-xl ${isCheckedIn ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'}`}>
                            <div className="flex items-start gap-3">
                              <div className="flex flex-col items-center mt-1">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: isCheckedIn ? '#10B981' : evType.color }} />
                                {sIdx < getFilteredSessions(day.sessions).length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1 min-h-[20px]" />}
                              </div>
                              <div className="flex-1">
                                {session.time && <p className="text-xs font-bold" style={{ color: evType.color }}>{session.time}</p>}
                                <p className="text-sm font-semibold text-white">{session.title}</p>
                                {session.description && <p className="text-xs text-gray-500 mt-0.5">{session.description}</p>}
                                {session.location && <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{session.location}</p>}
                              </div>
                              {/* Check-in button */}
                              {!isStaff && (
                                isCheckedIn ? (
                                  <Badge className="text-[9px] bg-emerald-100 text-emerald-700 self-start"><CheckCircle className="w-3 h-3 mr-0.5" /> In</Badge>
                                ) : (
                                  <Button size="sm" onClick={() => checkinToSession(ev.id, sessionId)} disabled={checkingIn === sessionId}
                                    className="text-[10px] h-7 rounded-lg self-start" style={{ backgroundColor: evType.color }}>
                                    {checkingIn === sessionId ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Check In'}
                                  </Button>
                                )
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          ))}

          {/* Uploaded schedule document */}
          {ev.uploaded_schedule_url && (
            <Card className="p-4 rounded-2xl bg-white shadow-sm">
              <a href={ev.uploaded_schedule_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-blue-600 hover:text-blue-800">
                <FileText className="w-5 h-5" />
                <span className="text-sm font-semibold">View Full Schedule Document</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </a>
            </Card>
          )}

          {/* Event Survey */}
          {!isStaff && !ev.survey_completed && (ev.survey_questions?.length > 0 || true) && (
            <Card className="p-4 rounded-2xl bg-amber-50 border border-amber-200 cursor-pointer hover:shadow-sm" onClick={() => setShowSurvey(true)}>
              <div className="flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-amber-500" />
                <div className="flex-1">
                  <p className="font-bold text-amber-900 text-sm">Event Feedback</p>
                  <p className="text-xs text-amber-700">Share your experience (+10 pts)</p>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-500" />
              </div>
            </Card>
          )}
          {ev.survey_completed && (
            <Card className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
                <p className="text-sm font-semibold text-emerald-800">Survey submitted — thanks for your feedback!</p>
              </div>
            </Card>
          )}
        </div>

        {/* Survey Modal */}
        <Dialog open={showSurvey} onOpenChange={setShowSurvey}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Event Feedback</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold">How would you rate this event?</Label>
                <div className="flex gap-2 mt-2">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setSurveyForm({...surveyForm, rating: n})}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${surveyForm.rating >= n ? 'bg-amber-400 text-gray-900 scale-110' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                      <Star className="w-5 h-5" fill={surveyForm.rating >= n ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>
              {(ev.survey_questions || []).map((q, i) => {
                const questionText = typeof q === 'string' ? q : q.question || '';
                return (
                  <div key={i}>
                    <Label className="text-xs font-bold">{questionText}</Label>
                    <Textarea value={surveyForm.responses[questionText] || ''} onChange={e => setSurveyForm({...surveyForm, responses: {...surveyForm.responses, [questionText]: e.target.value}})} placeholder="Your answer..." className="mt-1" rows={2} />
                  </div>
                );
              })}
              <div>
                <Label className="text-xs font-bold">Any other feedback?</Label>
                <Textarea value={surveyForm.feedback} onChange={e => setSurveyForm({...surveyForm, feedback: e.target.value})} placeholder="What did you like? What could be better?" className="mt-1" rows={3} />
              </div>
              <Button onClick={submitSurvey} className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold">Submit Feedback (+10 pts)</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ==================== EVENTS LIST ====================
  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      <div className="px-4 py-3 flex items-center gap-3 shadow-sm" style={{ backgroundColor: '#2D2824' }}>
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-gray-100 hover:bg-white/20" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="flex-1">
          <p className="text-gray-900 font-bold text-sm">Events</p>
          <p className="text-gray-400 text-[10px]">Orientations, courses & program events</p>
        </div>
        {isStaff && (
          <Button size="sm" onClick={() => setShowCreateModal(true)} className="bg-orange-600 hover:bg-orange-700 text-white text-xs rounded-lg" data-testid="create-event-btn">
            <Plus className="w-3 h-3 mr-1" /> Create
          </Button>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-3">
        {events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-bold text-gray-400">No events yet</p>
            <p className="text-sm text-gray-400">{isStaff ? 'Create your first event.' : 'Check back soon!'}</p>
          </div>
        ) : (
          events.map((ev, idx) => {
            const evType = EVENT_TYPES[ev.event_type] || EVENT_TYPES.custom;
            const totalSessions = (ev.schedule || []).reduce((sum, d) => sum + (d.sessions?.length || 0), 0);
            const checkedIn = (ev.my_checkins || []).length;
            return (
              <motion.div key={ev.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                <Card onClick={() => { setSelectedEvent(null); fetch(`${API}/api/gps/events/${ev.id}`, { headers: hdr() }).then(r => r.json()).then(d => { setSelectedEvent(d); setExpandedDay(0); }); }}
                  className="p-4 rounded-2xl bg-white shadow-sm cursor-pointer hover:shadow-md transition-all" data-testid={`event-${ev.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl" style={{ backgroundColor: `${evType.color}15` }}>{evType.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900 text-sm">{ev.title}</p>
                        <Badge className="text-[9px]" style={{ backgroundColor: `${evType.color}15`, color: evType.color }}>{evType.label}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[10px] text-gray-400">
                        {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                        {ev.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(ev.start_date).toLocaleDateString()}</span>}
                        {totalSessions > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{totalSessions} sessions</span>}
                      </div>
                      {/* Check-in progress */}
                      {!isStaff && totalSessions > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(checkedIn / totalSessions) * 100}%`, backgroundColor: evType.color }} />
                          </div>
                          <span className="text-[10px] text-gray-500">{checkedIn}/{totalSessions}</span>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  </div>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-500" /> Create Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Template Selector */}
            <div>
              <Label className="text-xs font-bold mb-2 block">Event Template</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(EVENT_TYPES).map(([k, v]) => (
                  <button key={k} onClick={() => applyTemplate(k)}
                    className={`p-3 rounded-xl text-left transition-all border ${form.event_type === k ? 'ring-2 ring-orange-400 bg-orange-50 border-orange-200' : 'bg-white border-gray-100 hover:bg-gray-50'}`}>
                    <p className="text-sm font-bold">{v.icon} {v.label}</p>
                    <p className="text-[9px] text-gray-400 mt-0.5">{v.description}</p>
                  </button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs font-bold">Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. SYEP Orientation Week" className="mt-1" /></div>
            <div><Label className="text-xs font-bold">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What is this event about?" className="mt-1" rows={2} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-bold">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Main Hall / Campus" className="mt-1" /></div>
              <div><Label className="text-xs font-bold">Virtual Link (optional)</Label><Input value={form.virtual_link || ''} onChange={e => setForm({...form, virtual_link: e.target.value})} placeholder="https://zoom.us/..." className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-bold">Start *</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-xs font-bold">End</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs font-bold">Cohorts / Groups (comma-separated, optional)</Label><Input value={form.cohorts.join(', ')} onChange={e => setForm({...form, cohorts: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} placeholder="Group A, Group B, Group C" className="mt-1" /></div>
            <div><Label className="text-xs font-bold">Survey Questions (auto-filled from template, edit below)</Label><Textarea value={(form.survey_questions || []).map(q => typeof q === 'string' ? q : q.question || '').join('\n')} onChange={e => setForm({...form, survey_questions: e.target.value.split('\n').filter(Boolean).map(q => ({ question: q, type: 'text' }))})} placeholder="What was your favorite part?&#10;What could be improved?" className="mt-1" rows={3} /></div>

            {/* Schedule builder */}
            <div className="border-t pt-3">
              <p className="text-xs font-bold text-gray-700 mb-2">SCHEDULE</p>
              {form.schedule.map((day, i) => (
                <div key={i} className="p-2 rounded-lg bg-gray-50 mb-2 flex items-center justify-between">
                  <div><p className="text-xs font-bold">{day.day_title}</p><p className="text-[10px] text-gray-500">{day.sessions?.length || 0} sessions</p></div>
                  <button onClick={() => setForm({...form, schedule: form.schedule.filter((_, idx) => idx !== i)})}><Trash2 className="w-3 h-3 text-red-400" /></button>
                </div>
              ))}
              <div className="p-3 rounded-lg border-2 border-dashed border-gray-200 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={dayForm.day_title} onChange={e => setDayForm({...dayForm, day_title: e.target.value})} placeholder="Day 1: Theme" className="text-xs" />
                  <Input type="date" value={dayForm.day_date} onChange={e => setDayForm({...dayForm, day_date: e.target.value})} className="text-xs" />
                </div>
                {dayForm.sessions.map((s, i) => (
                  <div key={i} className="grid grid-cols-4 gap-1">
                    <Input value={s.time} onChange={e => { const ss = [...dayForm.sessions]; ss[i] = {...ss[i], time: e.target.value}; setDayForm({...dayForm, sessions: ss}); }} placeholder="9:00 AM" className="text-[10px]" />
                    <Input value={s.title} onChange={e => { const ss = [...dayForm.sessions]; ss[i] = {...ss[i], title: e.target.value}; setDayForm({...dayForm, sessions: ss}); }} placeholder="Session title" className="text-[10px] col-span-2" />
                    <Input value={s.location} onChange={e => { const ss = [...dayForm.sessions]; ss[i] = {...ss[i], location: e.target.value}; setDayForm({...dayForm, sessions: ss}); }} placeholder="Room" className="text-[10px]" />
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setDayForm({...dayForm, sessions: [...dayForm.sessions, { time: '', title: '', description: '', location: '', type: 'course', cohort: '' }]})} className="text-[10px] h-6"><Plus className="w-2 h-2 mr-1" /> Session</Button>
                  <Button size="sm" onClick={addDayToSchedule} className="text-[10px] h-6 bg-orange-600 text-white"><Plus className="w-2 h-2 mr-1" /> Add Day</Button>
                </div>
              </div>
            </div>
            <Button onClick={createEvent} className="w-full bg-emerald-600 hover:bg-emerald-700 text-gray-900 font-bold">Create Event & Notify Youth</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Navigation role="youth" />
    </div>
  );
};

export default EventsPage;
